import { useEffect, useRef, useState } from "react";
import { gsap } from "@/lib/gsap";

export default function CustomCursor() {
  const dot = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState("default");

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches && window.innerWidth >= 1024;
    if (!fine) return undefined;
    setEnabled(true);
    document.documentElement.classList.add("has-cursor");
    return () => document.documentElement.classList.remove("has-cursor");
  }, []);

  useEffect(() => {
    if (!enabled || !dot.current) return undefined;

    gsap.set(dot.current, { x: window.innerWidth / 2, y: window.innerHeight / 2 });
    const xTo = gsap.quickTo(dot.current, "x", { duration: 0.35, ease: "power3" });
    const yTo = gsap.quickTo(dot.current, "y", { duration: 0.35, ease: "power3" });
    const onMove = (e) => { xTo(e.clientX); yTo(e.clientY); };

    let magnet = null;
    const onOver = (e) => {
      const view = e.target.closest?.("[data-cursor='view']");
      const link = e.target.closest?.("a, button, [data-magnetic]");
      const solid = e.target.closest?.("header, [data-cursor='solid']");
      if (view) setMode("view");
      else if (link) { setMode("link"); magnet = link; }
      else if (solid) setMode("solid");
      else setMode("default");
    };
    const onOut = (e) => {
      const link = e.target.closest?.("a, button, [data-magnetic]");
      if (link && magnet === link) {
        gsap.to(link, { x: 0, y: 0, duration: 0.6, ease: "elastic.out(1, 0.4)" });
        magnet = null;
      }
    };
    const onMagnet = (e) => {
      if (!magnet) return;
      const r = magnet.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      gsap.to(magnet, { x: dx * 0.18, y: dy * 0.18, duration: 0.4, ease: "power2.out" });
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mousemove", onMagnet);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousemove", onMagnet);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
    };
  }, [enabled]);

  if (!enabled) return null;
  const size = mode === "view" ? 96 : mode === "link" ? 40 : mode === "solid" ? 18 : 16;
  return (
    <div
      ref={dot}
      data-testid="custom-cursor"
      className="fixed top-0 left-0 z-[300] pointer-events-none flex items-center justify-center rounded-full font-mono2 text-[10px] tracking-[0.25em] text-black -translate-x-1/2 -translate-y-1/2"
      style={{
        width: size, height: size, marginLeft: -size / 2, marginTop: -size / 2,
        background: mode === "view" ? "#fff" : mode === "link" ? "rgba(2,170,207,0.28)" : "#02AACF",
        border: mode === "link" ? "1.5px solid #02AACF" : "2px solid rgba(255,255,255,0.9)",
        boxShadow: mode === "view" ? "0 0 0 1px rgba(0,0,0,0.15)" : "0 0 14px rgba(2,170,207,0.95), 0 0 3px rgba(0,0,0,0.5)",
        mixBlendMode: "normal",
        transition: "width .35s cubic-bezier(.22,1,.36,1), height .35s cubic-bezier(.22,1,.36,1), margin .35s cubic-bezier(.22,1,.36,1), background .3s",
      }}
    >
      {mode === "view" && "VIEW"}
    </div>
  );
}
